import { Button, notification, Spin, Table } from 'antd'
import React, { useMemo, useState } from 'react'
import {
  useCreateFeatureToggleMutation,
  useFetchFeatureToggleListQuery,
  useLazyFetchFeatureToggleByIdQuery,
  useUpdateFeatureToggleMutation,
} from 'domain/FeatureToggle/featureToggleApi'
import {
  useFetchUserListQuery,
  useFetchUserRolesListQuery,
} from 'domain/User/userApi'
import Card from 'components/Card'
import featureCodes from 'constants/featureCodes'
import Heading from 'components/Heading'
import useFeatureAccessControlEnabled from 'hooks/useFeatureAccessControlEnabled'
import userRoles from 'constants/userRoles'
import { userRoleSelector } from 'domain/User/selectors'
import { useSelector } from 'react-redux'

import { useTranslation } from 'react-i18next'

import { FEATURE_TYPE } from './featureToggleConstants'
import FeatureToggleDrawer from './FeatureToggleDrawer'

import './FeatureToggle.less'
import '../../global/styles/global.less'

const FeatureToggle = () => {
  const { t } = useTranslation()

  const userRole = useSelector(userRoleSelector)
  const isAdmin = userRole === userRoles.ADMIN

  // Support role is not introduced as of now
  const isSupport = useFeatureAccessControlEnabled(
    featureCodes.ENABLE_FEATURE_TOGGLE_VIEW
  )

  const { data: userList = [], isLoading: isRequestingUserList } =
    useFetchUserListQuery()
  const { data: userRolesList = [], isLoading: isRequestingUserRolesList } =
    useFetchUserRolesListQuery()

  // RTK Query - auto-fetches on mount; refetches automatically after a
  // successful createFeatureToggle mutation.
  const {
    data: featureToggleList = [],
    isLoading: isRequestingFeatureToggleList,
  } = useFetchFeatureToggleListQuery()

  const [triggerCreateFeatureToggle, { isLoading: isCreatingFeatureToggle }] =
    useCreateFeatureToggleMutation()

  const [triggerUpdateFeatureToggle, { isLoading: isUpdatingFeatureToggle }] =
    useUpdateFeatureToggleMutation()

  const [fetchFeatureToggleById] = useLazyFetchFeatureToggleByIdQuery()

  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [drawerMode, setDrawerMode] = useState('create')
  const [drawerInitialValues, setDrawerInitialValues] = useState({})
  const [loadingEditId, setLoadingEditId] = useState(null)

  const sortedFeatureToggleList = useMemo(
    () =>
      [...featureToggleList].sort((a, b) => {
        const timeA = new Date(a.updatedAt ?? a.createdAt ?? 0).getTime()
        const timeB = new Date(b.updatedAt ?? b.createdAt ?? 0).getTime()

        return timeB - timeA
      }),
    [featureToggleList]
  )

  const specificUsersOptions = useMemo(() => {
    const list = Array.isArray(userList) ? userList : []

    return list
      .filter(u => u && u.id && u.username)
      .map(u => ({ label: u.username, value: u.id }))
  }, [userList])

  const roleOptions = useMemo(() => {
    const list = Array.isArray(userRolesList) ? userRolesList : []

    return list
      .filter(item => item && item.id && item.role)
      .map(item => ({ label: item.role, value: item.id }))
  }, [userRolesList])

  const openCreateDrawer = () => {
    setDrawerMode('create')
    setDrawerInitialValues({
      featureType: Object.keys(FEATURE_TYPE).find(
        k => FEATURE_TYPE[k] === FEATURE_TYPE.frontend
      ),
    })
    setIsDrawerOpen(true)
  }

  const openEditDrawer = async record => {
    setLoadingEditId(record?.id)

    try {
      const detail = await fetchFeatureToggleById(record?.id).unwrap()

      setDrawerMode('edit')
      setDrawerInitialValues({
        description: detail.description ?? '',
        featureCode: detail.featureCode ?? '',
        featureType:
          Object.keys(FEATURE_TYPE).find(
            k => FEATURE_TYPE[k] === detail.featureType
          ) ?? '',
        id: detail.id,
        isEnabled: Boolean(detail.enabled),
        roles: (detail.roles || []).map(r => r.id),
        usernames: (detail.users || []).map(u => u.id),
      })
      setIsDrawerOpen(true)
    } catch {
      notification.error({
        description: t('global.error.getRequestErrorDescription'),
        message: t('global.error.getRequestErrorMessage'),
      })
    } finally {
      setLoadingEditId(null)
    }
  }

  const openViewDrawer = async record => {
    setLoadingEditId(record?.id)

    try {
      const detail = await fetchFeatureToggleById(record?.id).unwrap()

      setDrawerMode('view')
      setDrawerInitialValues({
        description: detail.description ?? '',
        featureCode: detail.featureCode ?? '',
        featureType:
          Object.keys(FEATURE_TYPE).find(
            k => FEATURE_TYPE[k] === detail.featureType
          ) ?? '',
        isEnabled: Boolean(detail.enabled),
        roles: (detail.roles || []).map(r => r.id),
        usernames: (detail.users || []).map(u => u.id),
      })
      setIsDrawerOpen(true)
    } catch {
      notification.error({
        description: t('global.error.getRequestErrorDescription'),
        message: t('global.error.getRequestErrorMessage'),
      })
    } finally {
      setLoadingEditId(null)
    }
  }

  const closeDrawer = () => {
    setIsDrawerOpen(false)
  }

  const handleEditSubmit = async formValues => {
    const payload = {
      description: formValues.description,
      enabled: Boolean(formValues.isEnabled),
      featureCode: formValues.featureCode,
      featureType: FEATURE_TYPE[formValues.featureType],
      id: drawerInitialValues.id,
      roleIds: formValues.roles || [],
      userIds: formValues.usernames || [],
    }

    await triggerUpdateFeatureToggle(payload).unwrap()
    notification.success({
      message: t('featureToggle.notifications.updateSuccess'),
    })
    closeDrawer()
  }

  const handleCreateSubmit = async formValues => {
    const payload = {
      description: formValues.description,
      enabled: Boolean(formValues.isEnabled),
      featureCode: formValues.featureCode,
      featureType: FEATURE_TYPE[formValues.featureType],
      roleIds: formValues.roles || [],
      userIds: formValues.usernames || [],
    }

    await triggerCreateFeatureToggle(payload).unwrap()
    notification.success({
      message: t('featureToggle.notifications.createSuccess'),
    })
    closeDrawer()
  }

  const columns = [
    {
      dataIndex: 'featureCode',
      key: 'featureCode',
      sorter: (a, b) =>
        (a.featureCode ?? '').localeCompare(b.featureCode ?? ''),
      title: t('featureToggle.table.featureCode'),
    },
    {
      dataIndex: 'description',
      key: 'description',
      title: t('featureToggle.table.featureDescription'),
    },
    {
      dataIndex: 'featureType',
      key: 'featureType',
      render: value => {
        if (value === FEATURE_TYPE.frontend)
          return t('featureToggle.drawer.fields.frontend')
        if (value === FEATURE_TYPE.backend)
          return t('featureToggle.drawer.fields.backend')

        return ''
      },
      title: t('featureToggle.table.featureType'),
    },
    {
      dataIndex: 'enabled',
      key: 'isEnabled',
      render: value => {
        const label = value
          ? t('featureToggle.status.enabled')
          : t('featureToggle.status.disabled')

        return (
          <span
            className={
              value
                ? 'feature-toggle__status--enabled'
                : 'feature-toggle__status--disabled'
            }
          >
            {label}
          </span>
        )
      },
      title: t('featureToggle.table.isEnabled'),
    },
    {
      dataIndex: 'action',
      key: 'action',
      render: (_, record) => {
        if (isAdmin) {
          return (
            <Button
              onClick={() => openEditDrawer(record)}
              size="small"
              type="link"
            >
              {t('featureToggle.table.edit')}
            </Button>
          )
        }

        if (isSupport) {
          return (
            <Button
              onClick={() => openViewDrawer(record)}
              size="small"
              type="link"
            >
              {t('featureToggle.table.view')}
            </Button>
          )
        }

        return null
      },
      title: t('featureToggle.table.action'),
    },
  ]

  const isLoading =
    Boolean(loadingEditId) || isCreatingFeatureToggle || isUpdatingFeatureToggle

  return (
    <>
      {isLoading && (
        <div className="feature-toggle__loading-overlay">
          <Spin size="large" />
        </div>
      )}
      <div className="page__container">
        <Heading level={1}>{t('featureToggle.pageTitle')}</Heading>

        <Card
          className="feature-toggle__card"
          title={t('featureToggle.cardTitle')}
          headerActionButton={
            isAdmin
              ? { label: t('featureToggle.create'), onClick: openCreateDrawer }
              : null
          }
        >
          <Spin spinning={isRequestingFeatureToggleList}>
            <Table
              columns={columns}
              dataSource={sortedFeatureToggleList}
              pagination={{ pageSize: 10, showSizeChanger: false }}
              rowKey="id"
            />
          </Spin>
        </Card>

        <FeatureToggleDrawer
          initialValues={drawerInitialValues}
          isRequestingRolesList={isRequestingUserRolesList}
          isRequestingUserList={isRequestingUserList}
          mode={drawerMode}
          onClose={closeDrawer}
          open={isDrawerOpen}
          roleOptions={roleOptions}
          specificUsersOptions={specificUsersOptions}
          onSubmit={
            drawerMode === 'create'
              ? handleCreateSubmit
              : drawerMode === 'edit'
              ? handleEditSubmit
              : undefined
          }
        />
      </div>
    </>
  )
}

export default FeatureToggle
